import React, { useMemo, useState } from 'react';
import { Item, Board, useCanvasStore } from '@/stores/canvasStore';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { SyncChoiceModal } from '@/components/canvas/SyncChoiceModal';
import { BoardPickerList } from './BoardPicker';
import { useIsMobile } from '@/hooks/use-mobile';
import { getLinkResultTitle } from '@/lib/searchResultPreview';
import { getAddToCanvasMode, getPlacementOnBoard } from '@/lib/discoverToCanvas';
import { toast } from 'sonner';

interface AddToCanvasDialogProps {
  item: Item;
  onClose: () => void;
}

/** Short human label for an item, used in the duplicate-vs-sync prompt. */
function getItemLabel(item: Item): string {
  const content = (item.content || {}) as Record<string, unknown>;
  if (item.item_type === 'link') return getLinkResultTitle(item);
  return (content.title as string) || (content.filename as string) || (content.alt as string) || 'Item';
}

/**
 * "Add to canvas…" from Discover: pick a board, then place the item there.
 * Items that already live on another board get the same duplicate-vs-sync
 * choice as adding a canvas search result from another board.
 */
export function AddToCanvasDialog({ item, onClose }: AddToCanvasDialogProps) {
  const { boards, allItems, getVisibleBoards, createItem, updateItem, openItemOnCanvas } = useCanvasStore();
  const isMobile = useIsMobile();
  const [choiceBoard, setChoiceBoard] = useState<Board | null>(null);

  // Home first, then every board that is reachable from the UI
  const pickableBoards = useMemo(() => {
    const home = boards.find(b => b.parent_id === null);
    return home ? [home, ...getVisibleBoards()] : getVisibleBoards();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boards, allItems]);

  const boardLabel = (board: Board) => (board.parent_id === null ? 'Home Board' : board.name);
  const sourceBoard = boards.find(b => b.id === item.board_id);

  const announce = (board: Board, placed: Pick<Item, 'id' | 'board_id'>, message: string) => {
    toast.success(`${message} "${boardLabel(board)}"`, isMobile ? undefined : {
      action: { label: 'Open', onClick: () => { openItemOnCanvas(placed); } },
    });
    onClose();
  };

  const createOnBoard = async (board: Board, asSync: boolean) => {
    const position = getPlacementOnBoard(allItems, board.id);
    const created = await createItem({
      board_id: board.id,
      item_type: asSync ? 'synced_item' : item.item_type,
      position_x: position.x,
      position_y: position.y,
      width: item.width,
      height: item.height,
      content: asSync
        ? { source_item_id: item.id, source_board_id: item.board_id }
        : (item.content as Record<string, unknown>),
    });
    // createItem already toasts on failure
    if (created) announce(board, created, asSync ? 'Synced to' : 'Added to');
  };

  const handleSelectBoard = async (boardId: string) => {
    const board = boards.find(b => b.id === boardId);
    if (!board) return;

    const mode = getAddToCanvasMode(item, board.id);
    if (mode === 'choose') {
      setChoiceBoard(board);
      return;
    }
    if (mode === 'copy') {
      await createOnBoard(board, false);
      return;
    }

    // No board yet: move the item itself rather than leaving a stray copy in Discover
    const position = getPlacementOnBoard(allItems, board.id, item.id);
    await updateItem(item.id, { board_id: board.id, position_x: position.x, position_y: position.y });
    announce(board, { id: item.id, board_id: board.id }, 'Added to');
  };

  if (choiceBoard) {
    return (
      <SyncChoiceModal
        isOpen={true}
        onClose={onClose}
        itemName={getItemLabel(item)}
        sourceBoardName={sourceBoard ? boardLabel(sourceBoard) : 'another board'}
        onDuplicate={() => createOnBoard(choiceBoard, false)}
        onSync={() => createOnBoard(choiceBoard, true)}
      />
    );
  }

  return (
    <Dialog open={true} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-sm" data-testid="add-to-canvas-dialog">
        <DialogHeader>
          <DialogTitle>Add to canvas</DialogTitle>
          <DialogDescription>Choose the board to place this item on.</DialogDescription>
        </DialogHeader>
        <BoardPickerList
          boards={pickableBoards}
          selectedBoardId={item.board_id}
          onSelectBoard={handleSelectBoard}
        />
      </DialogContent>
    </Dialog>
  );
}
